import { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { EChart, type EChartsOption } from '@/components/charts/EChart';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import type { RecurringRunoffMonth } from '@/lib/api';
import { buildRunoffTooltipHtml } from './runoffTooltip';

const RUNOFF_SHADES = ['#b45309', '#d97706', '#f59e0b', '#fbbf24', '#fcd34d'];
/** Below this width the months get cramped horizontally, so we lay the bars on their side. */
const COMPACT_CHART_WIDTH = 440;

/** Compact currency for the value axis ("R$1,5k") — keeps the R$ but stays short so labels don't overflow. */
function axisMoney(v: number): string {
  if (v === 0) return 'R$0';
  if (Math.abs(v) >= 1000) return `R$${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k`;
  return `R$${v.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;
}

/**
 * "Liberação de parcelas": the run-off split by installment over the full remaining horizon — you can
 * see which parcelas roll off each month, not just the total. Per-month totals reconcile with the
 * faturas "Compromisso por mês" (first 6 months); on mobile the bars go horizontal so a long horizon
 * stays readable.
 */
export function RunoffChart({ runoff }: { runoff: RecurringRunoffMonth[] }) {
  const formatCurrency = useFormatCurrency();
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const isCompact = chartWidth > 0 && chartWidth < COMPACT_CHART_WIDTH;

  useEffect(() => {
    const element = chartRef.current;
    if (!element) return undefined;
    const update = () => {
      const next = Math.round(element.getBoundingClientRect().width);
      setChartWidth((prev) => (prev === next ? prev : next));
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver((entries) => {
      const next = Math.round(entries[0]?.contentRect.width ?? 0);
      setChartWidth((prev) => (prev === next ? prev : next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const relief = useMemo(() => {
    let best = { month: '', value: 0 };
    for (let i = 1; i < runoff.length; i += 1) {
      const drop = runoff[i - 1].total - runoff[i].total;
      if (drop > best.value) best = { month: runoff[i].monthLabel, value: drop };
    }
    return best;
  }, [runoff]);

  // One stacked series per installment (stable id → first-seen label/colour).
  const seriesDefs = useMemo(() => {
    const seen = new Map<string, string>();
    for (const month of runoff) {
      for (const segment of month.segments) {
        if (!seen.has(segment.id)) seen.set(segment.id, segment.label);
      }
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [runoff]);

  const option = useMemo<EChartsOption>(() => {
    const labels = runoff.map((m) => m.monthLabel);
    const series = seriesDefs.map((def, idx) => ({
      name: def.label,
      type: 'bar' as const,
      stack: 'runoff',
      barMaxWidth: isCompact ? 18 : 36,
      data: runoff.map((m) => m.segments.find((s) => s.id === def.id)?.amount ?? 0),
      itemStyle: {
        color: RUNOFF_SHADES[idx % RUNOFF_SHADES.length],
        borderRadius: isCompact
          ? idx === 0
            ? [3, 0, 0, 3]
            : idx === seriesDefs.length - 1
              ? [0, 3, 3, 0]
              : [0, 0, 0, 0]
          : idx === 0
            ? [0, 0, 3, 3]
            : idx === seriesDefs.length - 1
              ? [3, 3, 0, 0]
              : [0, 0, 0, 0],
      },
    }));

    const valueAxis = {
      type: 'value' as const,
      splitNumber: isCompact ? 4 : 5,
      axisLabel: { formatter: axisMoney, fontSize: isCompact ? 10 : 11, hideOverlap: true },
      splitLine: { lineStyle: { color: '#F0F1F0' } },
    };
    const categoryAxis = {
      type: 'category' as const,
      data: labels,
      axisTick: { show: false },
      axisLabel: { fontSize: 11, color: '#6B736E' },
      inverse: isCompact,
    };

    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, confine: true, formatter: buildRunoffTooltipHtml },
      grid: { left: 4, right: isCompact ? 16 : 8, top: 8, bottom: 6, containLabel: true },
      xAxis: isCompact ? valueAxis : categoryAxis,
      yAxis: isCompact ? categoryAxis : valueAxis,
      series,
    };
  }, [runoff, seriesDefs, isCompact]);

  if (runoff.length === 0) return null;

  // Horizontal layout grows with the number of months so the full horizon stays legible on mobile.
  const height = isCompact ? Math.max(220, runoff.length * 30 + 24) : 224;

  return (
    <Card variant="flat" className="rounded-[var(--radius-3xl)] border-border bg-surface p-5 shadow-[var(--shadow-1)] sm:p-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-text-primary">Liberação de parcelas</h2>
          <p className="mt-0.5 text-xs text-text-tertiary">Quanto cada parcela compromete nos próximos meses</p>
        </div>
        {relief.value > 0 && (
          <p className="text-xs text-text-secondary">
            Maior alívio em <span className="font-semibold text-text-primary">{relief.month}</span>:{' '}
            <span className="font-mono text-success-text tabular-nums">{formatCurrency(relief.value)}/mês</span> liberados
          </p>
        )}
      </div>
      <div ref={chartRef} className="w-full" style={{ height }}>
        <EChart option={option} notMerge />
      </div>
    </Card>
  );
}
